import React, { useState } from "react";

import {
  Backdrop,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  makeStyles,
} from "@material-ui/core";

import {
  Button,
  useListContext,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";

import {
  ArrowDropDownIcon,
  CheckIcon,
  DoubleCheckIcon,
} from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import { InvoiceStatus, ResourceType } from "../../../utils/constants";
import { formatCurrency } from "../../../utils/utils";
import { customLogic } from "./customLogic";
import { getUnpaidOnComplete, planComplete } from "./invoicePaymentUtils";
import { RecordPaymentDialog } from "./RecordPaymentDialog";
import { CONFLICT_MESSAGE, saveInvoiceChange } from "./saveInvoiceChange";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  split: {
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  arrow: {
    padding: 4,
    marginLeft: -4,
  },
}));

const stop = (e) => {
  e.stopPropagation();
  e.nativeEvent?.stopImmediatePropagation?.();
};

/**
 * Marks one invoice PAID with a zero balance, over the invoice as read now, so
 * the key fields are current and a short payment is noted in the history.
 * Resolves "completed", "already" when it was completed before, or "conflict"
 * when it kept changing and nothing was saved.
 */
const completeInvoice = async (invoiceId) => {
  const paidDate = new Date().toISOString();
  const result = await saveInvoiceChange(invoiceId, (invoice) =>
    planComplete(invoice, paidDate),
  );
  if (result.skip === "completed") return "already";
  if (result.skip) return result.skip;

  const { invoice } = result;
  const unpaid = getUnpaidOnComplete(invoice);
  logHistory({
    text: unpaid
      ? `Invoice paid/completed with ${formatCurrency(unpaid)} unpaid.`
      : "Invoice paid/completed.",
    parentId: invoice.id,
    parentType: ResourceType.INVOICE,
  });
  return "completed";
};

const COMPLETE_MESSAGES = {
  completed: ["Invoice updated successfully.", "info"],
  already: ["Invoice was already completed.", "info"],
  conflict: [CONFLICT_MESSAGE, "warning"],
};

const isButtonDisabled = (loading) => {
  return loading || customLogic();
};

/**
 * Complete, with a menu for Record Payment. Payments go on PENDING invoices
 * only; a draft has to be sent first.
 */
export const MarkPaidButton = ({ record }) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const saveInvoice = async () => {
    try {
      setLoading(true);
      if (record) {
        const outcome = await completeInvoice(record.id);
        notify(...COMPLETE_MESSAGES[outcome]);
      }
      refresh();
    } catch (error) {
      logError("invoices:MarkPaidButton:saveInvoice", error, notify);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (e) => {
    stop(e);
    setAnchorEl(null);
    saveInvoice();
  };

  const handleMenuOpen = (e) => {
    stop(e);
    setAnchorEl(e.currentTarget);
  };

  const handleRecordPayment = (e) => {
    stop(e);
    setAnchorEl(null);
    setPaymentOpen(true);
  };

  if (!record || record.status === InvoiceStatus.PAID) return null;

  const disabled = isButtonDisabled(loading);
  // An invoice synced to an accounting system takes its payments from there.
  const isSynced = Boolean(record.externalStatus);
  const canRecordPayment = record.status === InvoiceStatus.PENDING && !isSynced;
  const recordPaymentHint = isSynced
    ? "Paid in your accounting system"
    : record.status !== InvoiceStatus.PENDING
      ? "Send invoice first"
      : null;

  return (
    <span className={classes.split}>
      <Button
        label="Complete"
        color="primary"
        onClick={handleUpdate}
        disabled={disabled}
        loading={loading}
      >
        <CheckIcon />
      </Button>
      <IconButton
        aria-label="Payment actions"
        color="primary"
        size="small"
        className={classes.arrow}
        onClick={handleMenuOpen}
        disabled={disabled}
      >
        <ArrowDropDownIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(e) => {
          e?.stopPropagation?.();
          setAnchorEl(null);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleRecordPayment} disabled={!canRecordPayment}>
          <ListItemText
            primary="Record Payment…"
            secondary={recordPaymentHint}
          />
        </MenuItem>
        <MenuItem onClick={handleUpdate}>
          <ListItemText primary="Complete" />
        </MenuItem>
      </Menu>
      {paymentOpen ? (
        <RecordPaymentDialog
          open={paymentOpen}
          invoiceId={record.id}
          onClose={() => setPaymentOpen(false)}
        />
      ) : null}
    </span>
  );
};

export const BulkMarkPaidButton = (props) => {
  const classes = useStyles();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const {
    selectedIds, // an array listing the ids of the selected rows, e.g. [123, 456]
    onUnselectItems,
  } = useListContext();
  const [loading, setLoading] = useState(false);

  const updateInvoices = async () => {
    try {
      setLoading(true);

      // Each invoice is read fresh: list rows carry no payments, and the key
      // fields must be current for the status indexes.
      const outcomes = await Promise.all(
        selectedIds.map((invoiceId) => completeInvoice(invoiceId)),
      );

      const conflicts = outcomes.filter((o) => o === "conflict").length;
      if (conflicts) {
        notify(
          `${conflicts} of ${outcomes.length} invoices changed while saving and were not completed. Check them and try again.`,
          "warning",
        );
      } else {
        notify("Invoices updated successfully.", "info");
      }
      onUnselectItems();
      refresh();
    } catch (error) {
      logError(
        "invoices:BulkMarkPaidButton:updateInvoices",
        error,
        notify,
        redirect,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (e) => {
    stop(e);
    updateInvoices();
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button
        label="Complete"
        onClick={handleUpdate}
        color={props.color || "default"}
        disabled={isButtonDisabled(loading)}
        loading={loading}
      >
        <DoubleCheckIcon />
      </Button>
    </>
  );
};
